import React, { useEffect, useState } from "react";
import { MessageSquare, Mail, MessageCircle, Bell, Plus, Trash2, Power, FlaskConical, ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Eye, EyeOff, Star, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MessageTemplates from "./MessageTemplates";

const CHANNEL_META = {
  sms: { label: "SMS", icon: MessageSquare, color: "blue", bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-700" },
  email: { label: "Email", icon: Mail, color: "indigo", bg: "bg-indigo-50", border: "border-indigo-200", badge: "bg-indigo-100 text-indigo-700" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "green", bg: "bg-green-50", border: "border-green-200", badge: "bg-green-100 text-green-700" },
  fcm: { label: "Push (FCM)", icon: Bell, color: "orange", bg: "bg-orange-50", border: "border-orange-200", badge: "bg-orange-100 text-orange-700" },
};

const PROVIDER_FORMS = {
  fast2sms: {
    label: "Fast2SMS", type: "sms",
    credentials: [{ key: "api_key", label: "API Key / Authorization", placeholder: "Am6cwCtT4Hk53Ib..." }],
    config: [
      { key: "route", label: "Route", type: "select", options: [{ v: "q", l: "Quick (Transactional)" }, { v: "dlt", l: "DLT (Registered Template)" }], default: "q" },
      { key: "sender_id", label: "DLT Sender ID", placeholder: "ZPCARE (for DLT route)" },
      { key: "dlt_msg_id", label: "DLT Message ID", placeholder: "210441 (for DLT route)" },
      { key: "app_name", label: "App Name (variable)", placeholder: "DishLCO" },
    ],
  },
  msg91: {
    label: "MSG91", type: "sms",
    credentials: [{ key: "api_key", label: "Auth Key", placeholder: "MSG91 auth key" }],
    config: [
      { key: "sender_id", label: "Sender ID", placeholder: "DISHLC" },
      { key: "template_id", label: "Template ID", placeholder: "optional DLT template ID" },
    ],
  },
  smtp: {
    label: "SMTP Email", type: "email",
    credentials: [
      { key: "user", label: "SMTP Username / Email", placeholder: "your@gmail.com" },
      { key: "password", label: "SMTP Password / App Password", placeholder: "••••••••", secret: true },
    ],
    config: [
      { key: "host", label: "SMTP Host", placeholder: "smtp.gmail.com" },
      { key: "port", label: "Port", type: "number", placeholder: "587", default: "587" },
      { key: "from_email", label: "From Email", placeholder: "noreply@dishlco.com" },
      { key: "from_name", label: "From Name", placeholder: "DishLCO" },
    ],
  },
  bonrix: {
    label: "Bonrix WhatsApp", type: "whatsapp",
    credentials: [{ key: "api_key", label: "Bonrix API Key", placeholder: "61246094e3a44f..." }],
    config: [
      { key: "base_url", label: "API Base URL", placeholder: "http://bot.bonrix.in/wapp/api/send" },
    ],
  },
  fcm: {
    label: "Firebase Cloud Messaging (FCM)", type: "fcm",
    credentials: [
      { key: "service_account_json", label: "Service Account JSON", placeholder: '{"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}', multiline: true },
    ],
    config: [
      { key: "api_key", label: "Web API Key", placeholder: "AIzaSy..." },
      { key: "auth_domain", label: "Auth Domain", placeholder: "your-project.firebaseapp.com" },
      { key: "project_id", label: "Project ID", placeholder: "your-firebase-project" },
      { key: "storage_bucket", label: "Storage Bucket", placeholder: "your-project.appspot.com" },
      { key: "messaging_sender_id", label: "Messaging Sender ID", placeholder: "123456789012" },
      { key: "app_id", label: "App ID", placeholder: "1:123456789012:web:abc123..." },
      { key: "vapid_key", label: "VAPID Key", placeholder: "BNg...xxx (from Firebase Console → Cloud Messaging → Web Push certificates)" },
    ],
    helpText: "Paste the Service Account JSON from Firebase Console → Project Settings → Service Accounts → Generate New Private Key. Config fields are from the Web App settings under General tab.",
  },
};

function ProviderCard({ p, onToggle, onDelete, onTest, onSetDefault }) {
  const [expanded, setExpanded] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("Test message from DishLCO notification system.");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const cm = CHANNEL_META[p.type] || CHANNEL_META.sms;
  const Icon = cm.icon;

  const runTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a target mobile/email"); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post(`/superadmin/notification/providers/${p.id}/test`, {
        to: testTo.trim(), message: testMsg,
      });
      setTestResult(data);
      if (data.ok) toast.success("Test sent successfully!");
      else toast.error(data.message || "Test failed");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  return (
    <div className={`bg-white rounded-xl border ${p.is_enabled ? cm.border : "border-slate-200"} overflow-hidden shadow-sm transition-all`}>
      <div className="px-5 py-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cm.bg}`}>
          <Icon className={`w-5 h-5 text-${cm.color}-600`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800">{p.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cm.badge}`}>{cm.label}</span>
            {p.is_default && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                <Star className="w-3 h-3" /> Default
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.is_enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
              {p.is_enabled ? "Active" : "Disabled"}
            </span>
          </div>
          <div className="text-xs text-slate-400 font-mono mt-0.5">{p.provider} · added {(p.created_at || '').slice(0, 10)}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!p.is_default && p.is_enabled && (
            <button onClick={() => onSetDefault(p)} title="Set as Default"
              className="p-1.5 rounded-md text-amber-400 hover:bg-amber-50 hover:text-amber-600 transition-colors">
              <Star className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => onToggle(p)} title={p.is_enabled ? "Disable" : "Enable"}
            className={`p-1.5 rounded-md transition-colors ${p.is_enabled ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-100"}`}>
            <Power className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(p)} title="Delete"
            className="p-1.5 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 transition-colors">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/60 space-y-4">
          {/* Credentials (masked) */}
          {p.credentials && Object.keys(p.credentials).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Credentials</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(p.credentials).map(([k, v]) => (
                  <div key={k} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs">
                    <span className="text-slate-500">{k}: </span>
                    <span className="font-mono text-slate-700">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Config */}
          {p.config && Object.keys(p.config).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Configuration</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(p.config).map(([k, v]) => (
                  <div key={k} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs">
                    <span className="text-slate-500">{k}: </span>
                    <span className="font-mono text-slate-700">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Test panel */}
          <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5" /> Test This Provider
            </div>
            <div className="flex gap-2">
              <input value={testTo} onChange={e => setTestTo(e.target.value)}
                placeholder={p.type === "email" ? "test@example.com" : "9999999999"}
                className="flex-1 border border-slate-200 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-primary" />
              <button onClick={runTest} disabled={testing || !p.is_enabled}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover px-3 py-1.5 rounded-md transition-colors disabled:opacity-50">
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
                Send Test
              </button>
            </div>
            <input value={testMsg} onChange={e => setTestMsg(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-primary"
              placeholder="Test message body..." />
            {testResult && (
              <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-md ${testResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                {testResult.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddProviderModal({ onClose, onAdded }) {
  const [step, setStep] = useState(1); // 1=select, 2=configure
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [creds, setCreds] = useState({});
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  const provMeta = selectedProvider ? PROVIDER_FORMS[selectedProvider] : null;

  const save = async () => {
    if (!selectedProvider) return;
    const missingCred = provMeta.credentials.find(f => !creds[f.key]);
    if (missingCred) { toast.error(`${missingCred.label} is required`); return; }
    setSaving(true);
    try {
      await api.post("/superadmin/notification/providers", {
        provider: selectedProvider,
        is_enabled: true,
        is_default: isDefault,
        credentials: creds,
        config: cfg,
      });
      toast.success(`${provMeta.label} provider added!`);
      onAdded();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add provider");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h4 className="font-heading font-bold text-xl text-brand-nav">Add Notification Provider</h4>
        <p className="text-sm text-slate-500 mt-1">Configure a new SMS, Email, or WhatsApp provider.</p>

        {step === 1 && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            {Object.entries(PROVIDER_FORMS).map(([key, meta]) => {
              const cm = CHANNEL_META[meta.type];
              const Icon = cm.icon;
              return (
                <button key={key} onClick={() => { setSelectedProvider(key); setCreds({}); setCfg({}); setStep(2); }}
                  className={`p-4 text-left border rounded-xl hover:border-brand-primary hover:bg-brand-primary/5 transition-all group`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cm.bg} mb-2`}>
                    <Icon className={`w-4 h-4 text-${cm.color}-600`} />
                  </div>
                  <div className="font-semibold text-sm text-slate-800 group-hover:text-brand-primary">{meta.label}</div>
                  <div className={`text-xs mt-0.5 px-1.5 py-0.5 rounded-full inline-block ${cm.badge}`}>{cm.label}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && provMeta && (
          <div className="mt-5 space-y-4">
            <button onClick={() => setStep(1)} className="text-xs text-brand-primary hover:underline">← Change provider</button>

            <div className={`flex items-center gap-2 p-3 rounded-lg ${CHANNEL_META[provMeta.type].bg} border ${CHANNEL_META[provMeta.type].border}`}>
              <span className="text-sm font-semibold text-slate-700">{provMeta.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${CHANNEL_META[provMeta.type].badge}`}>{CHANNEL_META[provMeta.type].label}</span>
            </div>

            {/* Credentials */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                <span>Credentials</span>
                <button type="button" onClick={() => setShowSecrets(!showSecrets)} className="flex items-center gap-1 text-slate-400 hover:text-slate-600">
                  {showSecrets ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showSecrets ? "Hide" : "Show"}
                </button>
              </div>
              <div className="space-y-3">
                {provMeta.credentials.map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{f.label} *</label>
                    {f.multiline ? (
                      <textarea
                        rows={6}
                        value={creds[f.key] || ''}
                        onChange={e => setCreds(p => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-y"
                      />
                    ) : (
                      <input
                        type={f.secret && !showSecrets ? "password" : "text"}
                        value={creds[f.key] || ''}
                        onChange={e => setCreds(p => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Config */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Configuration</div>
              <div className="space-y-3">
                {provMeta.config.map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                    {f.type === 'select' ? (
                      <select value={cfg[f.key] || f.default || ''} onChange={e => setCfg(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
                        {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    ) : (
                      <input type={f.type || 'text'} value={cfg[f.key] || ''}
                        onChange={e => setCfg(p => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {provMeta.helpText && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                ℹ️ {provMeta.helpText}
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} className="rounded" />
              <span className="text-sm text-slate-700">Set as default provider for {CHANNEL_META[provMeta.type].label}</span>
            </label>

            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 rounded-lg py-2 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg py-2 font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Provider
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NotificationProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [otpChannel, setOtpChannel] = useState({ channel: 'sms', options: [] });
  const [otpSaving, setOtpSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/notification/providers");
      setProviders(data);
    } catch { toast.error("Failed to load providers"); }
    finally { setLoading(false); }
  };

  const loadOtpChannel = async () => {
    try {
      const { data } = await api.get("/superadmin/notification/otp-channel");
      setOtpChannel(data);
    } catch { }
  };

  const saveOtpChannel = async (val) => {
    setOtpSaving(true);
    try {
      await api.put("/superadmin/notification/otp-channel", { channel: val });
      setOtpChannel(prev => ({ ...prev, channel: val }));
      toast.success("OTP delivery channel updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setOtpSaving(false); }
  };

  useEffect(() => { load(); loadOtpChannel(); }, []);

  const handleToggle = async (p) => {
    try {
      await api.post(`/superadmin/notification/providers/${p.id}/toggle`);
      toast.success(`${p.label} ${p.is_enabled ? "disabled" : "enabled"}`);
      load();
    } catch { toast.error("Failed to toggle provider"); }
  };

  const handleDelete = async (p) => {
    if (!confirm(`Delete "${p.label}"? This will disable notifications for this channel if it's the only provider.`)) return;
    try {
      await api.delete(`/superadmin/notification/providers/${p.id}`);
      toast.success("Provider deleted");
      load();
    } catch { toast.error("Failed to delete provider"); }
  };

  const handleSetDefault = async (p) => {
    try {
      await api.put(`/superadmin/notification/providers/${p.id}`, { is_default: true, is_enabled: true });
      toast.success(`${p.label} set as default`);
      load();
    } catch { toast.error("Failed to set default"); }
  };

  const tabs = ['all', 'sms', 'email', 'whatsapp', 'fcm'];
  const filtered = activeTab === 'all' ? providers : providers.filter(p => p.type === activeTab);

  const channelStats = {
    sms: providers.filter(p => p.type === 'sms' && p.is_enabled).length,
    email: providers.filter(p => p.type === 'email' && p.is_enabled).length,
    whatsapp: providers.filter(p => p.type === 'whatsapp' && p.is_enabled).length,
    fcm: providers.filter(p => p.type === 'fcm' && p.is_enabled).length,
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Notification Providers</h3>
          <p className="text-slate-500 mt-1">Configure SMS, Email, and WhatsApp providers. Admins use these to send messages — they cannot configure credentials themselves.</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold text-sm px-5 py-2.5 rounded-lg shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Add Provider
        </button>
      </div>

      {/* Channel status cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(CHANNEL_META).map(([ch, cm]) => {
          const Icon = cm.icon;
          const count = channelStats[ch];
          return (
            <div key={ch} className={`rounded-xl border ${count > 0 ? cm.border : "border-slate-200"} p-4 ${count > 0 ? cm.bg : "bg-white"} flex items-center gap-3`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${count > 0 ? "bg-white" : "bg-slate-100"}`}>
                <Icon className={`w-5 h-5 ${count > 0 ? `text-${cm.color}-600` : "text-slate-400"}`} />
              </div>
              <div>
                <div className="font-semibold text-sm text-slate-800">{cm.label}</div>
                <div className={`text-xs font-medium ${count > 0 ? `text-${cm.color}-700` : "text-slate-400"}`}>
                  {count > 0 ? `${count} active provider${count > 1 ? 's' : ''}` : "Not configured"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* OTP Delivery Channel Preference */}
      <div className="mb-6 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h4 className="font-heading font-bold text-base text-violet-900">OTP Delivery Channel</h4>
            <p className="text-xs text-violet-600/70">Choose how system OTPs (login, password reset) are delivered to users.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(otpChannel.options || []).map(opt => (
            <button key={opt.value} onClick={() => saveOtpChannel(opt.value)} disabled={otpSaving}
              className={`text-left p-3 rounded-xl border-2 transition-all ${otpChannel.channel === opt.value
                  ? "border-violet-500 bg-white shadow-sm ring-1 ring-violet-200"
                  : "border-transparent bg-white/60 hover:bg-white hover:border-violet-200"
                } ${otpSaving ? "opacity-60 pointer-events-none" : ""}`}>
              <div className="flex items-center gap-2">
                {otpChannel.channel === opt.value && <CheckCircle2 className="w-4 h-4 text-violet-600 shrink-0" />}
                <span className={`text-sm font-semibold ${otpChannel.channel === opt.value ? "text-violet-800" : "text-slate-700"}`}>{opt.label}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-6">{opt.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${activeTab === t ? "bg-white text-brand-nav shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t === 'all' ? `All (${providers.length})` : `${CHANNEL_META[t]?.label} (${providers.filter(p => p.type === t).length})`}
          </button>
        ))}
      </div>

      {/* Provider list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <MessageSquare className="w-6 h-6 text-slate-400" />
          </div>
          <p className="font-semibold text-slate-600">No providers configured</p>
          <p className="text-sm text-slate-400 mt-1">Add your first notification provider to enable messaging.</p>
          <button onClick={() => setShowAdd(true)}
            className="mt-4 inline-flex items-center gap-2 bg-brand-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-brand-primaryHover transition-colors">
            <Plus className="w-4 h-4" /> Add Provider
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => (
            <ProviderCard key={p.id} p={p}
              onToggle={handleToggle}
              onDelete={handleDelete}
              onTest={() => { }}
              onSetDefault={handleSetDefault}
            />
          ))}
        </div>
      )}

      {showAdd && <AddProviderModal onClose={() => setShowAdd(false)} onAdded={load} />}

      {/* ── Message Templates Section ── */}
      <div className="mt-8 pt-8 border-t-2 border-slate-200">
        <MessageTemplates />
      </div>
    </div>
  );
}
